import { Dot } from "@hortis/ui/dot";
import { Folder, GitBranch1 } from "@hortis/ui/icons";
import { CircularProgress } from "@hortis/ui/progress";
import { toast } from "@hortis/ui/sonner";
import type { Editor } from "@tiptap/core";
import { GetEntitySuggestionsQuery } from "generated/graphql";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { PlantMaterialStatusToColor } from "src/components/status-chip";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { twMerge } from "tailwind-merge";

export const MentionList = forwardRef<
  HTMLDivElement & { onKeyDown: (e: { event: KeyboardEvent }) => boolean },
  {
    editor: Editor;
    items: GetEntitySuggestionsQuery["org"];
    command: any;
    fetching: boolean;
  }
>(({ items, ...props }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const taxa = items?.taxa?.nodes.slice(0, 5);
  const accessions = items?.site?.accessions?.nodes.slice(0, 5);
  const materials = items?.site?.plantMaterials?.nodes.slice(0, 5);

  const allItems = [
    ...(taxa ?? []),
    ...(accessions ?? []),
    ...(materials ?? []),
  ];

  const upHandler = () => {
    setSelectedIndex((selectedIndex + allItems.length - 1) % allItems.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % allItems.length);
  };

  const handleSelect = (item: (typeof allItems)[0]) => {
    if (item?.__typename === "Taxon") {
      props.command({
        id: item.id,
        label: item.scientificName,
        href: `${window.location.origin}/taxon/${item.id}`,
      });
    } else if (item?.__typename === "Accession") {
      props.command({
        id: item.id,
        label: item.accessionNumber,
        href: `${window.location.origin}/accession/${item.id}`,
      });
    } else if (item?.__typename === "PlantMaterial") {
      if (item.accession == null) {
        toast.error("Could not find material's accession");
        return;
      }
      props.command({
        id: item.id,
        label: createMaterialNumber(
          item.accession.accessionNumber,
          item.qualifier,
          "/",
        ),
        href: `${window.location.origin}/material/${item.id}`,
      });
    }
  };

  useEffect(() => {
    if (Number.isNaN(selectedIndex + 1)) {
      return;
    }
    const el = containerRef.current?.querySelector(
      `span:nth-of-type(${selectedIndex + 1})`,
    );
    if (el != null) {
      scrollIntoView(el, { behavior: "smooth", scrollMode: "if-needed" });
    }
  }, [selectedIndex]);

  const enterHandler = () => {
    const item = allItems[selectedIndex];
    if (item == null) {
      return;
    }
    handleSelect(item);
  };

  useEffect(() => setSelectedIndex(0), [items]);

  useImperativeHandle(ref as any, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div className="flex flex-col gap-0.5" ref={containerRef}>
      {taxa === undefined &&
      accessions === undefined &&
      materials === undefined ? (
        <div className="px-2 py-2 text-sm text-grey-500 font-medium flex items-center gap-3 justify-between">
          Fetching suggestions
          <CircularProgress size={16} />
        </div>
      ) : null}
      {taxa != null &&
      taxa.length === 0 &&
      accessions != null &&
      accessions.length === 0 &&
      materials != null &&
      materials.length === 0 ? (
        <div className="px-2 py-2 text-sm text-grey-500 font-medium flex items-center gap-3">
          No results
        </div>
      ) : null}
      {taxa != null && taxa.length > 0 ? (
        <>
          <div className="px-2 pb-1 pt-2.5 text-xs font-medium text-grey-500">
            Taxa
          </div>
          {taxa.map((item, index) => (
            <span
              data-cy="mention-item-taxon"
              className={twMerge(
                "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-grey-900 hover:bg-grey-100",
                index === selectedIndex ? "bg-grey-100" : "",
              )}
              key={item.id}
              onClick={() => handleSelect(item)}
            >
              <GitBranch1 className="inline h-4 w-4 text-grey-500" />
              <span className="whitespace-nowrap">{item.scientificName}</span>
              <span className="font-normal text-grey-500 truncate">
                {" "}
                {item.commonName}
              </span>
            </span>
          ))}
        </>
      ) : null}
      {accessions != null && accessions.length > 0 ? (
        <>
          <div className="px-2 pb-1 pt-2.5 text-xs font-medium text-grey-500">
            Accessions
          </div>
          {accessions.map((item, index) => (
            <span
              data-cy="mention-item-accession"
              className={twMerge(
                "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-grey-900 hover:bg-grey-100",
                index === selectedIndex - (taxa?.length ?? 0)
                  ? "bg-grey-100"
                  : "",
              )}
              key={item.id}
              onClick={() => handleSelect(item)}
            >
              <Folder className="inline h-4 w-4 text-grey-500" />
              <span className="whitespace-nowrap">{item.accessionNumber}</span>
              <span className="font-normal text-grey-500 truncate">
                {" "}
                {item.scientificName}
              </span>
            </span>
          ))}
        </>
      ) : null}
      {materials != null && materials.length > 0 ? (
        <>
          <div className="px-2 pb-1 pt-2.5 text-xs font-medium text-grey-500">
            Materials
          </div>
          {materials.map((item, index) => (
            <span
              data-cy="mention-item-material"
              className={twMerge(
                "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-grey-900 hover:bg-grey-100",
                index ===
                  selectedIndex -
                    (taxa?.length ?? 0) -
                    (accessions?.length ?? 0)
                  ? "bg-grey-100"
                  : "",
              )}
              key={item.id}
              onClick={() => handleSelect(item)}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <Dot color={PlantMaterialStatusToColor[item.status]} />
              </div>
              <span className="whitespace-nowrap">
                {createMaterialNumber(
                  item.accession?.accessionNumber ?? "",
                  item.qualifier,
                  "/",
                )}
              </span>
              <span className="font-normal text-grey-500 truncate">
                {" "}
                {item.accession?.scientificName}
              </span>
            </span>
          ))}
        </>
      ) : null}
    </div>
  );
});
